import { useServicesContext } from "@/modules/core/services/services.context"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { CreateSessionInputDto, UpdateSessionInputDto } from "@/modules/sessions/gateway/programInfiniteSessionDto"
import type { OnOffSessionInputDto } from "@/modules/sessions/gateway/programOnOffSessionDto"
import type { BlockSessionInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const useProgramSessionActions = () => {
  const { programInfiniteSessionApi, programOnOffSessionApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: deleteSession, isPending: isDeleteSessionPending } = useMutation({
    mutationFn: async ({ session }: { session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity }) => {
      if (session instanceof ProgramInfiniteSessionEntity) {
        return await programInfiniteSessionApi.deleteSession(session.id)
      }
      throw new Error("Session delete is not supported for this session type")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })

  const { mutateAsync: updateSession, isPending: isUpdateSessionPending } = useMutation({
    mutationFn: async ({
      id,
      programId,
      programType,
      session,
    }:
      | {
          id: string
          programId: string
          programType: ProgramType.PRO | ProgramType.VIRTUAL
          session: UpdateSessionInputDto
        }
      | {
          id: string
          programId: string
          programType: ProgramType.ONOFF
          session: OnOffSessionInputDto
        }) => {
      if (programType === ProgramType.VIRTUAL || programType === ProgramType.PRO) {
        return await programInfiniteSessionApi.updateSession(id, session)
      }
      if (programType === ProgramType.ONOFF) {
        return await programOnOffSessionApi.updateSession(id, programId, session)
      }
      throw new Error("Session update is not supported for this session type")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })

  const { mutateAsync: createSession, isPending: isCreateSessionPending } = useMutation({
    mutationFn: async ({
      programId,
      programType,
      session,
    }:
      | {
          programId: string
          programType: ProgramType.PRO | ProgramType.VIRTUAL
          session: CreateSessionInputDto
        }
      | {
          programId: string
          programType: ProgramType.ONOFF
          session: OnOffSessionInputDto
        }) => {
      if (programType === ProgramType.VIRTUAL || programType === ProgramType.PRO) {
        return await programInfiniteSessionApi.createSession(session)
      }
      if (programType === ProgramType.ONOFF) {
        return await programOnOffSessionApi.createSession(programId, session)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })

  // This mutation is used to add a block to a session
  // It uses the updateSession mutation to update the session and add the block to the session
  // Currently, the backend doesn't support adding a block to a session, so we use the updateSession mutation to add it
  const addBlockMutation = useMutation({
    mutationFn: async ({
      programId,
      session,
      block,
    }: {
      programId: string
      session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
      block: BlockSessionInputDto
    }) => {
      if (session instanceof ProgramInfiniteSessionEntity) {
        const updatedSession = ProgramInfiniteSessionEntity.fromEntityToUpdateInputDto(session)
        updatedSession.blocks.push(block)

        return await updateSession({
          id: session.id,
          programId: programId,
          programType: ProgramType.VIRTUAL,
          session: updatedSession,
        })
      }
      if (session instanceof ProgramOnOffSessionEntity) {
        const updatedSession = ProgramOnOffSessionEntity.fromEntityToInputDto(session)
        updatedSession.blocks.push(block)

        return await updateSession({
          id: session.id,
          programId: programId,
          programType: ProgramType.ONOFF,
          session: updatedSession,
        })
      }
      throw new Error("Session add block is not supported for this session type")
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })

  const updateBlockMutation = useMutation({
    mutationFn: async ({ sessionId, block }: { sessionId: string; block: BlockSessionInputDto }) => {
      await programInfiniteSessionApi.updateBlock(sessionId, block)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["programSession"] })
      void queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      void queryClient.invalidateQueries({ queryKey: ["trainingSessions"] })
    },
  })
  return {
    deleteSession,
    isDeleteSessionPending,
    updateSession,
    isUpdateSessionPending,
    createSession,
    isCreateSessionPending,
    addBlockMutation,
    updateBlockMutation,
  }
}
